import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import './ProjectDetails.css';

function ProjectDetails() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!projectId) {
            setError('Project ID is missing or invalid.');
            setLoading(false);
            return;
        }
        fetchProjectDetails();
    }, [projectId]);

    const fetchProjectDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            if (!projectId) {
                throw new Error('Project ID is missing or invalid');
            }
            const projectResponse = await projectsAPI.getProject(projectId);
            setProject(projectResponse);
        } catch (err) {
            setError('Failed to load project details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="project-details-loading"><LoadingSpinner /></div>;
    if (error) return <div className="project-details-error"><ErrorMessage message={error} /></div>;
    if (!project) return <div className="project-details-not-found">Project not found</div>;

    return (
        <div className="project-details-container">
            <div className="project-details-header">
                <h1>{project.title}</h1>
            </div>
            
            <div className="project-details-content">
                <div className="project-image-section">
                    <img 
                        src={project.imageUrl} 
                        alt={project.title}
                        className="project-details-image"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/default-project-image.jpg';
                        }}
                    />
                </div>

                <div className="project-info-section">
                    <div className="project-description">
                        <h2>About the Project</h2>
                        <p>{project.description}</p>
                        
                        {(project.startDate || project.endDate || project.responsible) && (
                            <div className="project-metadata">
                                {project.startDate && (
                                    <div className="metadata-item">
                                        <span className="metadata-label">Start Date:</span>
                                        <span className="metadata-value">{new Date(project.startDate).toLocaleDateString()}</span>
                                    </div>
                                )}
                                
                                {project.endDate && (
                                    <div className="metadata-item">
                                        <span className="metadata-label">End Date:</span>
                                        <span className="metadata-value">{new Date(project.endDate).toLocaleDateString()}</span>
                                    </div>
                                )}
                                
                                {project.responsible && (
                                    <div className="metadata-item">
                                        <span className="metadata-label">Responsible:</span>
                                        <span className="metadata-value">{project.responsible}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="project-actions">
                        {project.link && (
                            <a 
                                href={project.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="external-link-button"
                            >
                                Visit Project Website
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProjectDetails; 